import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {ImportContactsCsvJobModel, JobStatusesEnum} from '../../../../models/api/jobModel';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerCloseAction} from '../../../common/banners/bannerCloseAction';
import {BannerContent} from '../../../common/banners/bannerContent';
import {BannerContentTitle} from '../../../common/banners/bannerContentTitle';
import {BannerIcon} from '../../../common/banners/bannerIcon';
import {BannerProgress} from '../../../common/banners/bannerProgress';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';
import {BannerSecondaryText} from '../../../common/banners/bannerSecondaryText';
import {BannerText} from '../../../common/banners/bannerText';

/*
 * Props.
 */

interface ContactsCsvImportBannerProps {
  job: ImportContactsCsvJobModel;
  /** A fraction indicating the progress of the import. */
  progress: number;
  onDeleteJob: HandlerOf<number>;
}

/*
 * Component.
 */

export const ContactsCsvImportBanner: FC<ContactsCsvImportBannerProps> = (props) => {
  const {job, progress, onDeleteJob} = props;
  const isImporting = progress < 1;

  const onDismissClick = () => onDeleteJob(job.id);

  if (job.status === JobStatusesEnum.FAILED) {
    return renderErrorBanner(onDismissClick);
  }

  // If we are importing, render progress otherwise render success.
  return isImporting ? renderProgressBanner(progress) : renderCompletedBanner(onDismissClick);
};

function renderProgressBanner(progress: number) {
  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.INFORMATION}>
      <BannerProgress progress={progress} />
      <BannerIcon iconName="repeatCircleSmall" shouldRotate />
      <BannerText>
        <Trans id="banner-contacts-csv-import-importing">Importing your contacts...</Trans>
      </BannerText>
      <BannerSecondaryText>
        <Trans id="banner-contacts-csv-import-progress">{Math.floor(100 * progress)}%</Trans>
      </BannerSecondaryText>
    </Banner>
  );
}

function renderCompletedBanner(onDismissClick: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.INLINE}
          priority={BannerPrioritiesEnum.INFORMATION}
          color={theme.palette.green.shade40}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="checkmarkCircleSmall" />
          <BannerText>
            <Trans id="banner-contacts-csv-import-successful">Import successful!</Trans>
          </BannerText>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}

function renderErrorBanner(onDismissClick: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="warningFilledSmall" />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="banner-contacts-csv-import-failed">CSV import failed.</Trans>
            </BannerContentTitle>
            <Trans id="banner-contacts-csv-import-failed-try-again-message">Please try again.</Trans>
          </BannerContent>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}
